import { Trans } from '@lingui/react/macro';
import { ArrowCircleBrokenRight } from '@untitledui/icons/ArrowCircleBrokenRight';
import { Building06 } from '@untitledui/icons/Building06';
import { Home02 } from '@untitledui/icons/Home02';
import { MarkerPin04 } from '@untitledui/icons/MarkerPin04';
import { PieChart02 } from '@untitledui/icons/PieChart02';
import { PlaySquare } from '@untitledui/icons/PlaySquare';
import { Star04 } from '@untitledui/icons/Star04';

import type { KindOfResearch } from '@/api/core-api/types.gen';

import {
  KindOfResearch as KindOfResearchEnum,
  ResearchConductType,
} from '@/api/enums';

export interface ResearchMethodConfig {
  icon: React.ComponentType<{ className?: string }>;
  label: React.ReactNode;
  colorClass: string;
}

const MODERATED_CONFIG: Partial<
  Record<
    KindOfResearch,
    { remote: ResearchMethodConfig; inPerson: ResearchMethodConfig }
  >
> = {
  [KindOfResearchEnum.ONE_ON_ONE]: {
    remote: {
      icon: PlaySquare,
      label: <Trans>Video call</Trans>,
      colorClass: 'text-utility-success-600',
    },
    inPerson: {
      icon: MarkerPin04,
      label: <Trans>In person</Trans>,
      colorClass: 'text-utility-blue-600',
    },
  },
  [KindOfResearchEnum.FOCUS_GROUP]: {
    remote: {
      icon: PlaySquare,
      label: <Trans>Video call (Group)</Trans>,
      colorClass: 'text-utility-success-600',
    },
    inPerson: {
      icon: MarkerPin04,
      label: <Trans>In person (Group)</Trans>,
      colorClass: 'text-utility-blue-600',
    },
  },
};

/**
 * KindOfResearch enum values from core-api:
 * 1 = ONE_ON_ONE, 2 = FOCUS_GROUP, 4 = UNMODERATED_STUDY,
 * 8 = IN_RESPONDENT_HOME, 9 = SURVEY, 10 = DIARY_STUDY,
 * 11 = IN_RESPONDENT_OFFICE, 12 = AI_MODERATED_INTERVIEW
 */
const kindOfResearchConfig: Partial<
  Record<KindOfResearch, ResearchMethodConfig>
> = {
  4: {
    icon: ArrowCircleBrokenRight,
    label: <Trans>Online task</Trans>,
    colorClass: 'text-utility-warning-600',
  },
  8: {
    icon: Home02,
    label: <Trans>Home visit</Trans>,
    colorClass: 'text-utility-blue-light-600',
  },
  9: {
    icon: PieChart02,
    label: <Trans>Survey</Trans>,
    colorClass: 'text-utility-purple-600',
  },
  10: {
    icon: ArrowCircleBrokenRight,
    label: <Trans>Diary study</Trans>,
    colorClass: 'text-utility-warning-600',
  },
  11: {
    icon: Building06,
    label: <Trans>Office visit</Trans>,
    colorClass: 'text-utility-blue-light-600',
  },
  12: {
    icon: Star04,
    label: <Trans>AI Moderated</Trans>,
    colorClass: 'text-brand-secondary-600',
  },
};

const defaultConfig: ResearchMethodConfig = {
  icon: ArrowCircleBrokenRight,
  label: <Trans>Online task</Trans>,
  colorClass: 'text-utility-warning-600',
};

function isInPersonConduct(
  researchConductType?: ResearchConductType | number | string,
): boolean {
  return (
    researchConductType === ResearchConductType.IN_PERSON ||
    researchConductType === 1 ||
    researchConductType === '1'
  );
}

export function getResearchMethodConfig(
  kindOfResearch: KindOfResearch,
  researchConductType?: ResearchConductType | number | string,
): ResearchMethodConfig {
  const moderated = MODERATED_CONFIG[kindOfResearch];
  if (moderated) {
    return isInPersonConduct(researchConductType)
      ? moderated.inPerson
      : moderated.remote;
  }
  return kindOfResearchConfig[kindOfResearch] ?? defaultConfig;
}

const SIZE = {
  sm: { icon: 'size-4', label: 'text-xs' },
  md: { icon: 'size-5', label: 'text-sm' },
} as const;

interface ResearchMethodBadgeProps {
  kindOfResearch?: KindOfResearch;
  researchConductType?: ResearchConductType | number | string;
  size?: keyof typeof SIZE;
}

export function ResearchMethodBadge({
  kindOfResearch,
  researchConductType,
  size = 'md',
}: ResearchMethodBadgeProps) {
  if (!kindOfResearch) return null;

  const config = getResearchMethodConfig(kindOfResearch, researchConductType);
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`${SIZE[size].icon} ${config.colorClass}`} />
      <span className={`${SIZE[size].label} font-medium text-secondary`}>
        {config.label}
      </span>
    </div>
  );
}
